import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Crown } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { isProOrLifetime } from '@/constants/tiers';
import {
  PRO_TERMS, DEFAULT_PRO_TERM, findProTerm, DOUBLE_BENEFIT_PRICE, PRO_MONTHLY_PRICE,
} from '@/constants/proTerms';
import DoubleBenefitBanner from '@/components/promo/DoubleBenefitBanner';
import { useDoubleBenefit } from '@/hooks/useDoubleBenefit';
import { personalMonthPrice } from '@/lib/personalMonthPrice';
import { DOUBLE_BENEFIT_ENABLED } from '@/lib/siteConfig';
import { doubleBenefitOn } from '@/lib/rubProvider';
import { STRIPE_PRIMARY } from '@/lib/visitorRegion';
import { stripeDoubleBenefitLabel } from '@/lib/stripePromo';
import { oneTimeSaleShown, ONE_TIME_PRICE_LABEL } from '@/lib/calcUnlock';
import { giftDaysLeft } from '@/lib/proDays';
import ConsultationCard from '@/components/ConsultationCard';
import ProPitch from '@/components/promo/ProPitch';

/**
 * Витрина цен — ОДНА на главную (PricingBlock) и на /pricing (30.09.2026).
 * Раньше у главной и страницы цен были свои карточки, и они расходились.
 *
 * Четыре плашки в ряд на компьютере, столбиком на телефоне:
 *   1. Бесплатно — две части: без регистрации и «Регистрация в 1 клик»;
 *   2. Разовая — две части: 199 ₽ за одну программу и вопросы хорара;
 *   3. ПРО — переключатель Месяц / Год / Навсегда (constants/proTerms);
 *   4. Личная консультация (components/ConsultationCard).
 * ЛАЙТ 599 ₽ здесь не показывается: тариф и его оплата в коде остались,
 * но с витрины сняты.
 *
 * Оплату ПРО витрина не делает сама — её кнопки передаёт страница через
 * renderProActions(activeTerm): на /pricing это прямая оплата со
 * согласием на автопродление, на главной — переход на /checkout. Логика
 * оплаты при этом не менялась.
 */

// Условия цены 499 — backend/double_benefit.py: первые 17 дней после
// регистрации (день регистрации — нулевой) и только до первой оплаты тарифа;
// разовые покупки право не снимают.
export const DOUBLE_BENEFIT_LINE =
  `Двойная выгода в первый месяц: ПРО за ${DOUBLE_BENEFIT_PRICE} ₽ вместо ${PRO_MONTHLY_PRICE} ₽ + 14 дней в подарок (каждый день бонусные дни сокращаются)`;

// Вошедшему с подарочными днями — его личное число вместо «14» (тот же
// источник, что у счётчика под меню: gift_days_left, backend/gift_days.py).
// Гостю и остальным — строка как есть.
// Stripe-путь (иностранец на ру) — цены в его валюте: prices = { price,
// base } — «5,99 $» и «9,99 $».
export function doubleBenefitLineFor(giftDays, prices = null) {
  let line = DOUBLE_BENEFIT_LINE;
  if (prices && prices.price) {
    line = line.replace(`ПРО за ${DOUBLE_BENEFIT_PRICE} ₽`, `ПРО за ${prices.price}`)
      .replace(` вместо ${PRO_MONTHLY_PRICE} ₽`, prices.base ? ` вместо ${prices.base}` : '');
  }
  if (!giftDays) return line;
  const word = giftDays % 10 === 1 && giftDays % 100 !== 11 ? 'день'
    : [2, 3, 4].includes(giftDays % 10) && ![12, 13, 14].includes(giftDays % 100) ? 'дня' : 'дней';
  return line.replace('+ 14 дней в подарок', `+ ${giftDays} ${word} в подарок`);
}

// Плашка «Бесплатно», часть 1 — без регистрации.
export const FREE_GUEST_LINE = 'без регистрации — все программы, расшифровка около 15%';

// Часть 2 — «Регистрация в 1 клик, без карты».
export const FREE_POINTS = [
  'Расшифровка до 40% в каждой из 15 программ',
  '14 дней ежедневных прогнозов на почту',
  '1 вопрос хорара в сутки',
  'Натальная карта с расшифровкой и PDF',
  // Сборка без акции — строки про 499 нет; Stripe-путь и Робокасса
  // убирают её при показе (doubleBenefitOn).
  ...(DOUBLE_BENEFIT_ENABLED ? [DOUBLE_BENEFIT_LINE] : []),
];

export const ONE_TIME_POINTS = [
  'Одна программа открывается полностью — и остаётся твоей',
  'В Совместимости — одна пара',
];

// Плашка «Разовая», часть 2. Цены пакетов хорара — backend/routers/yookassa.py
// (HORARY_PACK_3 / _10).
export const HORARY_PACKS_LINE = 'сверх дневного лимита: 3 вопроса — 99 ₽, 10 вопросов — 249 ₽';

const PRO_SPAN = { month: 'на месяц', year: 'на год', forever: 'навсегда' };

const Point = ({ text, small }) => (
  <li className="flex items-start gap-1.5 leading-snug">
    <Check className={`${small ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-emerald-500 flex-shrink-0 mt-0.5`} />
    <span>{text}</span>
  </li>
);

const OwnedBadge = ({ testid }) => (
  <p className="text-xs text-emerald-700 text-center font-semibold bg-emerald-50 rounded-lg py-2" data-testid={testid}>
    ✓ у тебя есть
  </p>
);

export default function PricingCards({
  compact = false,
  place = 'pricing',
  availableTerms = PRO_TERMS,
  renderProActions,
}) {
  const navigate = useNavigate();
  // На ру разовое открытие не продаётся (Robokassa не знает разовых планов).
  const oneTimeShown = oneTimeSaleShown('natal');
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const proIsOwned = isAuthenticated && isProOrLifetime(user);
  const giftDays = isAuthenticated && user && user.tier_source === 'trial' ? giftDaysLeft(user) : null;
  // Покупательнице разовых — её цена с зачётом (backend/first_month_credit.py).
  const doubleBenefit = useDoubleBenefit();
  const creditLine = doubleBenefit.creditLine;
  const [proTerm, setProTerm] = useState(DEFAULT_PRO_TERM);
  // Срок берётся из ПЕРЕДАННЫХ сроков, а не из общего справочника: у
  // иностранца на ру /pricing передаёт сроки с ценой в его валюте.
  const activeTerm = availableTerms.find((t) => t.key === proTerm)
    || availableTerms.find((t) => t.key === DEFAULT_PRO_TERM)
    || findProTerm(DEFAULT_PRO_TERM);

  const personalPrice = personalMonthPrice(doubleBenefit, activeTerm, proIsOwned);
  // Stripe-путь: «Двойная выгода» в валюте посетителя (lib/stripePromo.js).
  const stripeDbLabel = STRIPE_PRIMARY ? stripeDoubleBenefitLabel() : null;
  const monthTerm = availableTerms.find((t) => t.key === 'month');
  // Сроки в рублях (витрина без цен Stripe) — без «вместо …», чтобы не смешать валюты.
  const monthBase = monthTerm && monthTerm.price && !String(monthTerm.price).includes('₽') ? monthTerm.price : null;
  const dbLinePrices = stripeDbLabel ? { price: stripeDbLabel, base: monthBase } : null;
  const showDbLine = doubleBenefitOn() || !!stripeDbLabel;
  const s = compact
    ? { card: 'p-3', title: 'text-base', price: 'text-xl', list: 'text-xs', btn: 'py-2 text-xs' }
    : { card: 'p-4', title: 'text-lg', price: 'text-2xl', list: 'text-sm', btn: 'py-2.5 text-sm' };
  const back = `${location.pathname}${location.search}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-stretch" data-testid="pricing-cards">
      {/* 1. Бесплатно */}
      <div className={`flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm ${s.card}`} data-testid="pricing-card-free">
        {/* Часть 1 — без регистрации */}
        <h3 className={`${s.title} font-bold text-slate-900`}>Бесплатно</h3>
        <p className={`${s.list} text-slate-700 mt-1 leading-snug`} data-testid="pricing-free-guest">
          {FREE_GUEST_LINE}
        </p>
        {/* Часть 2 — регистрация; заголовок того же размера, что «Бесплатно» */}
        <h3 className={`${s.title} font-bold text-slate-900 mt-3 pt-3 border-t border-slate-100`} data-testid="pricing-free-register-title">
          Регистрация в 1 клик, без карты
        </h3>
        <ul className={`${s.list} space-y-1 text-slate-700 flex-1 mt-1`}>
          {FREE_POINTS.filter((t) => t !== DOUBLE_BENEFIT_LINE || showDbLine).map((t) => (
            <Point key={t} text={t === DOUBLE_BENEFIT_LINE ? (creditLine || doubleBenefitLineFor(giftDays, dbLinePrices)) : t} small={compact} />
          ))}
        </ul>
        <div className="mt-3">
          {isAuthenticated ? (
            <OwnedBadge testid="pricing-free-owned" />
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/auth/register?redirect=${encodeURIComponent(back)}`)}
              className={`w-full ${s.btn} rounded-lg bg-slate-800 text-white font-semibold hover:bg-slate-900`}
              data-testid="pricing-register-btn"
            >
              Регистрация в 1 клик
            </button>
          )}
        </div>
      </div>

      {/* 2. Разовая. На ру (Robokassa) разово не продаём — части «199 ₽» нет,
          в карточке остаются вопросы хорара: пакеты на ру продаются. */}
      <div className={`flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm ${s.card}`} data-testid="pricing-card-one-time">
        {oneTimeShown && (
          <>
            <h3 className={`${s.title} font-bold text-slate-900`}>Разовая</h3>
            <div className="mt-1 mb-2 flex items-baseline gap-1.5">
              <span className={`${s.price} font-black text-slate-900`}>{ONE_TIME_PRICE_LABEL}</span>
              <span className="text-xs text-slate-500">одна программа полностью</span>
            </div>
            <ul className={`${s.list} space-y-1 text-slate-700`}>
              {ONE_TIME_POINTS.map((t) => <Point key={t} text={t} small={compact} />)}
            </ul>
          </>
        )}
        {/* Часть 2 — вопросы хорара; подзаголовок того же размера, что «Разовая» */}
        <div className="flex-1">
          <h3 className={`${s.title} font-bold text-slate-900 ${oneTimeShown ? 'mt-3 pt-3 border-t border-slate-100' : ''}`} data-testid="pricing-horary-title">
            Вопросы хорара
          </h3>
          <p className={`${s.list} text-slate-700 mt-1 leading-snug`} data-testid="pricing-horary-packs">
            {HORARY_PACKS_LINE}
          </p>
        </div>
        {oneTimeShown && (
          <>
            <p className="text-[11px] text-slate-500 leading-snug mt-2 pt-2 border-t">
              Кнопка «Открыть за {ONE_TIME_PRICE_LABEL}» — в самой программе, рядом с результатом.
            </p>
            <div className="mt-3">
              {proIsOwned ? (
                <p className="text-xs text-emerald-700 text-center font-medium" data-testid="pricing-one-time-included">
                  Входит в твой ПРО
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className={`w-full ${s.btn} rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200`}
                  data-testid="pricing-one-time-btn"
                >
                  Выбрать программу
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* 3. ПРО */}
      <div className="flex flex-col rounded-xl border-2 border-amber-300 bg-white shadow-lg" data-testid="pricing-card-pro">
        <div className={`bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-[10px] ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Crown className="w-4 h-4" />
            <span className={`${s.title} font-bold`}>ПРО</span>
            <span className="bg-white/25 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-testid="pro-popular-badge">
              выбирают чаще
            </span>
          </div>
          <div className="flex rounded-lg overflow-hidden border-2 border-white/60 mt-2" role="group"
            aria-label="Срок тарифа ПРО" data-testid="pro-term-switch">
            {availableTerms.map((term) => (
              <button
                key={term.key}
                type="button"
                onClick={() => setProTerm(term.key)}
                aria-pressed={activeTerm.key === term.key}
                className={`flex-1 py-1.5 text-xs md:text-sm font-bold transition-colors ${
                  activeTerm.key === term.key ? 'bg-white text-amber-700' : 'bg-white/15 text-white hover:bg-white/25'
                }`}
                data-testid={`pro-term-${term.key}`}
              >
                {term.label}
              </button>
            ))}
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            {/* Личная цена первого месяца (рф): ДВОЙНАЯ ВЫГОДА 499 ₽ или зачёт
                max(499 − S, 1) ₽ — крупно её, 999 зачёркнуто. Цену считает
                сервер (/auth/me double_benefit), касса примет ровно её. */}
            {personalPrice != null && (
              <span className="text-amber-100 text-base line-through" data-testid="pro-term-price-old">{activeTerm.price}</span>
            )}
            <span className={`${s.price} font-black`} data-testid="pro-term-price">
              {personalPrice != null ? (doubleBenefit.priceLabel || `${personalPrice} ₽`) : activeTerm.price}
            </span>
            <span className="text-amber-100 text-sm">{activeTerm.unit}</span>
          </div>
          {activeTerm.benefit && (
            <div className="text-amber-50 text-xs font-medium mt-1" data-testid="pro-term-benefit">{activeTerm.benefit}</div>
          )}
        </div>
        <div className={`flex-1 ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
          {/* ДВОЙНАЯ ВЫГОДА — про первый МЕСЯЦ: на сроках «год» и «навсегда» её нет. */}
          {activeTerm.key === 'month' && <DoubleBenefitBanner place={place} className="mb-2" />}
          <ul className={`${s.list} space-y-1 text-slate-700`}>
            <Point text={`Всё во всех программах — 100% информации ${PRO_SPAN[activeTerm.key] || ''}`.trim()} small={compact} />
            <Point text="Хорар без лимита" small={compact} />
          </ul>
          {/* Слоган и слово владелицы — у кнопки оформления ПРО. */}
          {!proIsOwned && <ProPitch className="mt-3" />}
        </div>
        {renderProActions && (
          <div className={`${compact ? 'px-3 pb-3' : 'px-4 pb-4'} mt-auto space-y-2`}>
            {renderProActions(activeTerm)}
          </div>
        )}
      </div>

      {/* 4. Личная консультация */}
      <ConsultationCard compact={compact} />
    </div>
  );
}
